import React, { useState, useEffect, useRef } from 'react'
import { supabase } from './supabase'
import { IdxListings } from './IdxListings'
import { canShowIdxListings } from './mlsLicense'
import { trackLead } from './metaPixel'
import EventFunnelLanding from './EventFunnelLanding'

// Down payment funnel copy. 2026-10-25: Spanish moved to usted (Josh's standing
// rule). The answer OPTIONS (q1/q2/q3 values) are unchanged: they are what is
// saved to funnel_leads and sent to FUB.
const T = {
  es: {
    badge: "Correduría Licenciada",
    eyebrow: "Programa de Enganche en Arizona",
    h1: 'Siga Pagando Renta o Empiece a Ser Dueño — Verifique Si Califica Para 0% de Enganche y Hasta 5% de Ayuda. ¡Aceptamos ITIN!',
    h1Figures: ['0% de Enganche', '5% de Ayuda'],
    sub: "Ayudamos a inquilinos en Arizona a conseguir ayuda para el enganche y programas de plusvalía — aunque antes los hayan rechazado.",
    bullets: [
      "Programas que cubren del 3 al 5% del precio para enganche y cierre cuando califique.",
      "Para compradores por primera vez y repetidos, incluyendo crédito bajo o ITIN en ciertos casos.",
      "Revisamos todos los programas de Arizona y le conectamos con la mejor opción."
    ],
    lfn: "Nombre", lln: "Apellido", lem: "Correo Electrónico", lph: "Número de Celular",
    lq1: "¿Cuándo espera comprar?", q1: ["0–3 meses","3–6 meses","6–12 meses","Solo explorando"],
    lq2: "¿Ha tenido casa en los últimos 3 años?", q2: ["Sí","No"],
    lq3: "¿Puntaje de crédito aproximado?", q3: ["Menos de 600","600–639","640–679","680+"],
    cta: "Obtener Mi Plan Gratuito →",
    step1: "Ver Mis Opciones →",
    step2: "Verificar Si Califico →",
    step2Msg: "Buenas noticias — según sus respuestas, puede haber programas que vale la pena revisar.",
    p1: "Paso 1 de 2", p2: "Paso 2 de 2",
    nudge: "Por favor responda las tres preguntas para continuar.",
    micro: "Sin obligación, sin verificación de crédito en este paso. Al enviar, acepta ser contactado por teléfono, texto y correo. Esto no es un compromiso de prestar.",
    t1: "100% Privado", t2: "Consulta Gratis", t3: "Sin Compromiso",
    tyTitle: "¡Ya Está Dentro!",
    tySub: "Su solicitud fue recibida. Un asesor le contactará en las próximas 24 horas.",
    visit: "Visitar Mi Perfil →",
    zillowPro: "Zillow Pro",
    zillowVerified: "Miembro Verificado",
    back: "← Regresar", time: "30 seg"
  },
  en: {
    badge: "Licensed Brokerage",
    eyebrow: "Arizona Down Payment Program",
    h1: 'Keep Paying Rent or Start Owning — Check If You Qualify for 0% Down and Up to 5% Assistance. ITIN Welcome!',
    h1Figures: ['0% Down', '5% Assistance'],
    sub: "We help Arizona renters access down payment help and equity programs — even if they've been turned down before.",
    bullets: [
      "Programs that can cover 3–5% of the purchase price for down payment and closing costs when you qualify.",
      "For first-time and repeat buyers, including low credit or ITIN borrowers in certain cases.",
      "We review every Arizona assistance program and connect you to the best option for your situation."
    ],
    lfn: "First Name", lln: "Last Name", lem: "Email Address", lph: "Cell Phone Number",
    lq1: "When are you hoping to buy?", q1: ["0–3 months","3–6 months","6–12 months","Just exploring"],
    lq2: "Have you owned a home in the last 3 years?", q2: ["Yes","No"],
    lq3: "Approx. credit score?", q3: ["Under 600","600–639","640–679","680+"],
    cta: "Get My Free Homeownership Plan →",
    step1: "See My Options →",
    step2: "Check If I Qualify →",
    step2Msg: "Good news — based on your answers, there may be programs worth reviewing.",
    p1: "Step 1 of 2", p2: "Step 2 of 2",
    nudge: "Please answer all three questions to continue.",
    micro: "No obligation, no credit check at this step. By submitting, you agree to be contacted by phone, text and email. This is not a commitment to lend.",
    t1: "100% Private", t2: "Free Consultation", t3: "No Obligation",
    tyTitle: "You're In!",
    tySub: "Your application has been received. An advisor will reach out within 24 hours.",
    visit: "Visit My Profile →",
    zillowPro: "Zillow Pro",
    zillowVerified: "Verified Member",
    back: "← Back", time: "30 sec"
  }
}

function resolveInitialLang(rowLangDefault) {
  const params = new URLSearchParams(window.location.search)
  const q = (params.get('lang') || '').toLowerCase()
  if (q === 'en' || q === 'es') return q
  const def = String(rowLangDefault || '').toLowerCase()
  return def === 'es' ? 'es' : 'en'
}

function isValidEmail(s) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim())
}

function renderBrokerage(name) {
  const s = String(name || '')
  if (s.startsWith('HomePros')) {
    return (
      <>
        <span>Home</span>
        <span className="fnl-brand-accent">Pros</span>
        {s.slice('HomePros'.length)}
      </>
    )
  }
  return s
}

function renderHeadline(text, figures) {
  if (!Array.isArray(figures) || figures.length === 0) return text
  const escaped = figures.map(f => f.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const re = new RegExp('(' + escaped.join('|') + ')', 'g')
  const parts = String(text).split(re)
  const figSet = new Set(figures)
  return parts.map((p, i) =>
    figSet.has(p) ? <span key={i} className="fnl-h1-accent">{p}</span> : <React.Fragment key={i}>{p}</React.Fragment>
  )
}

// Inline SVG icons — small, currentColor-driven so they inherit CSS color.
const IconCheck = ({ size = 14, stroke = 3 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
)
const IconCircleCheck = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="8 12.5 11 15.5 16 9.5"></polyline>
  </svg>
)
const IconLock = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
  </svg>
)
const IconMessage = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
  </svg>
)

// Drawn flags (emoji flags render as "US"/"MX" letters on Windows).
const FlagUS = () => (
  <svg className="fnl-flag" viewBox="0 0 38 20" aria-hidden="true">
    <rect width="38" height="20" fill="#b22234" />
    <g fill="#fff">{[1.54, 4.62, 7.69, 10.77, 13.85, 16.92].map(y => <rect key={y} y={y} width="38" height="1.54" />)}</g>
    <rect width="15.2" height="10.77" fill="#3c3b6e" />
    <g fill="#fff">
      {[[2.5, 2], [6.3, 2], [10.1, 2], [13.2, 2], [4.4, 4.2], [8.2, 4.2], [12, 4.2], [2.5, 6.4], [6.3, 6.4], [10.1, 6.4], [13.2, 6.4], [4.4, 8.6], [8.2, 8.6], [12, 8.6]]
        .map(([cx, cy]) => <circle key={cx + '-' + cy} cx={cx} cy={cy} r=".7" />)}
    </g>
  </svg>
)
const FlagMX = () => (
  <svg className="fnl-flag" viewBox="0 0 30 20" aria-hidden="true">
    <rect width="10" height="20" fill="#006847" />
    <rect x="10" width="10" height="20" fill="#fff" />
    <rect x="20" width="10" height="20" fill="#ce1126" />
    <ellipse cx="15" cy="10.6" rx="2.9" ry="2.5" fill="none" stroke="#3e8e41" strokeWidth=".9" />
    <ellipse cx="15" cy="9.6" rx="1.5" ry="1.7" fill="#8c5a2b" />
    <rect x="13.6" y="12.6" width="2.8" height=".8" rx=".4" fill="#1a73b5" />
  </svg>
)

// Brokerage logos (public/). A brokerage not listed here shows its name as
// text from the profile, so no office can ever show another office's logo.
const BROKERAGE_LOGOS = {
  'HomePros': '/homepros-logo.png',
  'HomePros Advantage': '/homepros-advantage-logo.png',
}

// Logo rule (same as EventFunnelLanding): the logo image only for an exact
// brokerage match in BROKERAGE_LOGOS; anything else shows the name as text.
function BrokerageMark({ brokerage, footer }) {
  const name = String(brokerage || '')
  if (BROKERAGE_LOGOS[name]) {
    return <img className={footer ? 'fnl-logo fnl-logo-foot' : 'fnl-logo'} src={BROKERAGE_LOGOS[name]} alt={name} />
  }
  return <span className={footer ? 'fnl-brand fnl-brand-foot' : 'fnl-brand'}>{renderBrokerage(brokerage)}</span>
}

export default function FunnelLanding({ slug }) {
  const [status, setStatus] = useState('loading') // 'loading' | 'active' | 'notfound'
  const [row, setRow] = useState(null)
  const [lang, setLang] = useState('en')

  useEffect(() => {
    let cancelled = false
    if (!slug) { setStatus('notfound'); return }
    supabase
      .rpc('get_funnel_public', { p_slug: slug })
      .then(({ data, error }) => {
        if (cancelled) return
        if (error) {
          console.error('[get_funnel_public] failed:', error)
          setStatus('notfound')
          return
        }
        const first = Array.isArray(data) ? data[0] : data
        if (!first) { setStatus('notfound'); return }
        setRow(first)
        setLang(resolveInitialLang(first.lang_default))
        setStatus('active')
      })
    return () => { cancelled = true }
  }, [slug])

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    q1: '', q2: '', q3: '',
  })
  const [touched, setTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitState, setSubmitState] = useState({ state: 'idle', msg: '' })
  const [step, setStep] = useState(1)              // 1 = qualifier questions, 2 = contact fields
  const [step1Touched, setStep1Touched] = useState(false)

  // Pinned top bar gets a frosted background once the page scrolls.
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Bring the form card into view when the step changes (not on first load).
  const cardRef = useRef(null)
  const firstStepRender = useRef(true)
  const done = submitState.state === 'success'
  useEffect(() => {
    if (firstStepRender.current) { firstStepRender.current = false; return }
    // Scroll the WINDOW, not scrollIntoView: scrollIntoView also scrolls the
    // overflow-hidden hero box itself, which uncovers a strip of the photo.
    const el = cardRef.current
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 84, behavior: 'smooth' })
  }, [step, done])

  const t = T[lang] || T.en

  const errors = {
    first_name: !form.first_name.trim(),
    last_name: !form.last_name.trim(),
    email: !form.email.trim() || !isValidEmail(form.email),
    phone: !form.phone.trim(),
  }
  const formValid = !errors.first_name && !errors.last_name && !errors.email && !errors.phone

  // Step 1 requires all three qualifier answers before advancing to contact fields.
  const step1Valid = !!(form.q1 && form.q2 && form.q3)
  function goToStep2() {
    setStep1Touched(true)
    if (!step1Valid) return
    setStep(2)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setTouched(true)
    if (!formValid || submitting || !row) return
    setSubmitting(true)
    setSubmitState({ state: 'loading', msg: '' })
    try {
      const { data: newId, error } = await supabase
        .rpc('submit_funnel_lead', {
          p_funnel_id:      row.funnel_id,
          p_first_name:     form.first_name.trim(),
          p_last_name:      form.last_name.trim(),
          p_email:          form.email.trim(),
          p_phone:          form.phone.trim(),
          p_language:       lang.toUpperCase(),
          p_timeline:       form.q1 || null,
          p_owned_recently: form.q2 || null,
          p_credit_band:    form.q3 || null,
          p_source:         row.source_tag,
        })
      if (error) throw error
      const leadId = Array.isArray(newId) ? newId[0] : newId
      setSubmitState({ state: 'success', msg: '' })

      // Fire the Meta Pixel Lead conversion — only on a successful final submit
      // (no-op unless a Pixel ID is configured in src/metaPixel.js).
      trackLead()

      // Fire-and-forget: push to Follow Up Boss. A FUB failure must NOT affect
      // the user-facing success message — admin can manually retry from the
      // Leads tab if anything goes sideways.
      if (leadId) {
        supabase.functions
          .invoke('send-funnel-lead-to-fub', { body: { lead_id: leadId } })
          .then(({ data, error: fnErr }) => {
            if (fnErr) console.error('[fub push] invoke failed:', fnErr)
            else if (data?.error) console.error('[fub push] function returned error:', data.error)
          })
          .catch((e) => console.error('[fub push] threw:', e))
      }
    } catch (err) {
      console.error('[funnel_leads] insert failed:', err)
      setSubmitState({
        state: 'error',
        msg: lang === 'es'
          ? 'Algo salió mal. Por favor inténtelo de nuevo.'
          : 'Something went wrong. Please try again.',
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (status === 'loading') {
    return (
      <>
        <style>{css}</style>
        <div className="fnl-shell">
          <div className="fnl-loading">…</div>
        </div>
      </>
    )
  }

  if (status === 'notfound') {
    return (
      <>
        <style>{css}</style>
        <div className="fnl-shell">
          <div className="fnl-notfound">
            <h1>Page not found</h1>
            <p>The page you are looking for does not exist.</p>
          </div>
        </div>
      </>
    )
  }

  // Event funnels (/funnel/team-events, /funnel/all-events) have their own page.
  // Placed after every hook above so hook order never changes.
  if (row.variant === 'event') return <EventFunnelLanding row={row} />

  const telHref = row.agent_phone ? `tel:${String(row.agent_phone).replace(/[^\d+]/g, '')}` : null
  const mailHref = row.agent_email ? `mailto:${row.agent_email}` : null
  const showZillow = submitState.state === 'success' && row.zillow_url
  return (
    <>
      <style>{css}</style>
      <div className="fnl-shell">
        <section className="fnl-stage">
          <div className="fnl-bg" aria-hidden="true"></div>
          <div className="fnl-shade" aria-hidden="true"></div>

          {/* TOP BAR — pinned: brokerage left, language right */}
          <div className="fnl-top-space" aria-hidden="true"></div>
          <div className={'fnl-topbar' + (scrolled ? ' scrolled' : '')}>
            <BrokerageMark brokerage={row.brokerage} />
            <div className="fnl-lang" role="group" aria-label="Language">
              <button type="button" onClick={() => setLang('en')} aria-pressed={lang === 'en'} className={lang === 'en' ? 'sel' : ''}>
                <FlagUS /><span>English</span>
              </button>
              <button type="button" onClick={() => setLang('es')} aria-pressed={lang === 'es'} className={lang === 'es' ? 'sel' : ''}>
                <FlagMX /><span>Español</span>
              </button>
            </div>
          </div>

          <div className="fnl-wrap">
            {/* HEAD — badges, headline, sub */}
            <div className="fnl-head">
              <div className="fnl-badges">
                <span className="fnl-badge">{t.badge}</span>
                <span className="fnl-eyebrow">{t.eyebrow}</span>
              </div>
              <h1 className="fnl-h1">{renderHeadline(t.h1, t.h1Figures)}</h1>
              <p className="fnl-sub">{t.sub}</p>
            </div>

            {/* FORM CARD — beside the head on desktop, right after it on phones */}
            <div className="fnl-card" ref={cardRef}>
              {done ? (
                <div className="fnl-thanks fnl-fade" key="done">
                  <div className="fnl-ring">
                    <svg viewBox="0 0 84 84" fill="none" aria-hidden="true">
                      <circle cx="42" cy="42" r="38" stroke="#fdbf00" strokeWidth="4" />
                      <path d="M27 43l10 10 20-21" stroke="#fff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <h2>{t.tyTitle}</h2>
                  <p>{t.tySub}</p>
                  {showZillow && (
                    <a className="fnl-zillow-cta" href={row.zillow_url} target="_blank" rel="noopener noreferrer">{t.visit}</a>
                  )}
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate className="fnl-fade" key={'step' + step}>
                  <div className="fnl-steplbl"><span>{step === 1 ? t.p1 : t.p2}</span><span>{step === 1 ? t.time : '✓'}</span></div>
                  <div className="fnl-bar"><i style={{ width: step === 1 ? '50%' : '100%' }}></i></div>

                  {step === 1 ? (
                    <>
                      {[['q1', t.lq1, t.q1], ['q2', t.lq2, t.q2], ['q3', t.lq3, t.q3]].map(([k, label, opts]) => (
                        <div className="fnl-q" key={k}>
                          <div className="fnl-q-label">{label}</div>
                          <div className="fnl-q-options">
                            {opts.map(opt => (
                              <button type="button" key={opt} className={form[k] === opt ? 'sel' : ''} aria-pressed={form[k] === opt}
                                onClick={() => setForm(f => ({ ...f, [k]: opt }))}>
                                <span>{opt}</span><span className="fnl-dot" aria-hidden="true"></span>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                      <button type="button" className="fnl-cta" onClick={goToStep2}>{t.step1}</button>
                      {step1Touched && !step1Valid && <p className="fnl-nudge" role="alert">{t.nudge}</p>}
                    </>
                  ) : (
                    <>
                      <div className="fnl-step2-msg"><IconCircleCheck size={18} /><span>{t.step2Msg}</span></div>
                      <div className="fnl-row">
                        <div className={'fnl-field' + (touched && errors.first_name ? ' invalid' : '')}>
                          <input id="fnl-fn" placeholder=" " value={form.first_name} autoComplete="given-name"
                            onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
                          <label htmlFor="fnl-fn">{t.lfn}</label>
                        </div>
                        <div className={'fnl-field' + (touched && errors.last_name ? ' invalid' : '')}>
                          <input id="fnl-ln" placeholder=" " value={form.last_name} autoComplete="family-name"
                            onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
                          <label htmlFor="fnl-ln">{t.lln}</label>
                        </div>
                      </div>
                      <div className={'fnl-field' + (touched && errors.email ? ' invalid' : '')}>
                        <input id="fnl-em" type="email" inputMode="email" placeholder=" " value={form.email} autoComplete="email"
                          onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                        <label htmlFor="fnl-em">{t.lem}</label>
                      </div>
                      <div className={'fnl-field' + (touched && errors.phone ? ' invalid' : '')}>
                        <input id="fnl-ph" type="tel" inputMode="tel" placeholder=" " value={form.phone} autoComplete="tel"
                          onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                        <label htmlFor="fnl-ph">{t.lph}</label>
                      </div>
                      <button type="submit" className="fnl-cta" disabled={submitting}>{t.step2}</button>
                      {submitState.state === 'error' && <p className="fnl-err" role="alert">{submitState.msg}</p>}
                      <button type="button" className="fnl-back" onClick={() => setStep(1)}>{t.back}</button>
                      <p className="fnl-micro">{t.micro}</p>
                    </>
                  )}

                  {/* TRUST ROW — both steps */}
                  <div className="fnl-trust">
                    <span><IconLock />{t.t1}</span>
                    <span><IconMessage />{t.t2}</span>
                    <span><IconCheck size={14} stroke={2.5} />{t.t3}</span>
                  </div>
                </form>
              )}
            </div>

            {/* EXTRA — bullets and Zillow Pro badge */}
            <div className="fnl-extra">
              <ul className="fnl-bullets">
                {t.bullets.map((b, i) => (
                  <li key={i}><span className="fnl-check-circle"><IconCheck /></span><span>{b}</span></li>
                ))}
              </ul>
              <div className="fnl-zillow-pro" aria-label={`${t.zillowPro} · ${t.zillowVerified}`}>
                <span className="fnl-zillow-icon"><IconCircleCheck size={24} /></span>
                <div className="fnl-zillow-text">
                  <div className="fnl-zillow-title">{t.zillowPro}</div>
                  <div className="fnl-zillow-sub">{t.zillowVerified}</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Listings — filled by IdxListings from get-idx-listings (the funnel
            owner's brokerage's IDX key). source = row.source_tag so listing
            leads route to THIS funnel's agent. 2026-10-25: shown to every
            visitor, ad traffic included (Josh) — it sits below the form, and the
            green "may qualify for down payment help" banners back up the ad.
            Hidden unless the owner's brokerage may show IDX listings (HomePros
            or HomePros Advantage). The band is passed in as `frame`, so it
            renders only when there are listings: no empty white box. */}
        {canShowIdxListings(row) && (
          <IdxListings page={`funnel:${slug}`} source={row.source_tag} lang={lang} variant="funnel"
            agentFirst={String(row.agent_name || '').trim().split(/\s+/)[0] || ''}
            frame={(content) => <div className="fnl-listings-wrap"><div className="fnl-listings-band"><div className="fnl-listings-inner">{content}</div></div></div>} />
        )}

        {/* FOOTER — brokerage and the funnel agent's contact */}
        <footer className="fnl-footer">
          <BrokerageMark brokerage={row.brokerage} footer />
          <div className="fnl-footer-agent">
            {row.agent_name && <span className="fnl-footer-name">{row.agent_name}</span>}
            {telHref && (<><span className="fnl-footer-sep" aria-hidden="true">·</span><a href={telHref}>{row.agent_phone}</a></>)}
            {mailHref && (<><span className="fnl-footer-sep" aria-hidden="true">·</span><a href={mailHref}>{row.agent_email}</a></>)}
          </div>
        </footer>
      </div>
    </>
  )
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
.fnl-shell{--blue:#1a82ff;--gold:#fdbf00;--orange:#ff7a1a;--ink:#0a1220;--glass:rgba(10,18,32,.62);--line:rgba(255,255,255,.14);--muted:rgba(255,255,255,.74);--green:#047857;
  min-height:100vh;background:var(--ink);color:#fff;font-family:'Plus Jakarta Sans',system-ui,-apple-system,'Segoe UI',sans-serif;-webkit-font-smoothing:antialiased;overflow-x:hidden}
.fnl-shell *{box-sizing:border-box}
.fnl-shell button,.fnl-shell input{font:inherit;color:inherit}

/* HERO */
.fnl-stage{position:relative;overflow:hidden;isolation:isolate;min-height:100vh}
.fnl-bg{position:absolute;inset:-4%;background:url(/dpa-hero.jpg) center 40%/cover no-repeat;animation:fnl-drift 28s ease-in-out infinite alternate;z-index:-3}
@keyframes fnl-drift{from{transform:scale(1.02)}to{transform:scale(1.12) translate(-2%,-1%)}}
.fnl-shade{position:absolute;inset:0;z-index:-2;background:linear-gradient(180deg,rgba(10,18,32,.72) 0%,rgba(10,18,32,.5) 30%,rgba(10,18,32,.72) 62%,var(--ink) 92%),radial-gradient(120% 70% at 0% 100%,rgba(26,130,255,.35),transparent 60%)}
@media(max-width:900px){.fnl-shade{background:linear-gradient(180deg,rgba(10,18,32,.82) 0%,rgba(10,18,32,.7) 35%,rgba(10,18,32,.78) 62%,var(--ink) 90%)}}

/* PINNED TOP BAR */
.fnl-topbar{position:fixed;top:0;left:0;right:0;z-index:50;display:flex;justify-content:space-between;align-items:center;gap:12px;padding:18px clamp(16px,4vw,48px);border-bottom:1px solid transparent;transition:background .25s,padding .25s,border-color .25s}
.fnl-topbar.scrolled{background:rgba(10,18,32,.82);-webkit-backdrop-filter:blur(16px) saturate(140%);backdrop-filter:blur(16px) saturate(140%);border-bottom-color:var(--line);padding-top:10px;padding-bottom:10px}
.fnl-top-space{height:calc(clamp(34px,5vw,48px) + 36px)}
.fnl-logo{height:clamp(34px,5vw,48px);width:auto;display:block;transition:height .25s;filter:drop-shadow(0 2px 10px rgba(0,0,0,.45))}
.fnl-topbar.scrolled .fnl-logo{height:clamp(30px,4vw,38px)}
.fnl-brand{font-size:20px;font-weight:800;letter-spacing:-.01em}
.fnl-brand-accent{color:var(--gold)}
.fnl-lang{display:flex;background:rgba(255,255,255,.12);-webkit-backdrop-filter:blur(14px);backdrop-filter:blur(14px);border:1px solid var(--line);border-radius:999px;padding:4px;flex:none}
.fnl-lang button{display:inline-flex;align-items:center;gap:7px;border:0;background:transparent;padding:7px 14px;border-radius:999px;font-size:13px;font-weight:700;cursor:pointer;color:#fff;opacity:.75;transition:.2s}
.fnl-lang button.sel{background:#fff;color:var(--ink);opacity:1}
.fnl-flag{width:22px;height:14px;border-radius:2px;box-shadow:0 0 0 1px rgba(0,0,0,.15);flex:none}
@media(max-width:420px){.fnl-lang button span{display:none}.fnl-lang button{padding:7px 10px}}

/* LAYOUT: head + extra left, card right; phones: head, card, extra */
.fnl-head{grid-area:head}.fnl-card{grid-area:card}.fnl-extra{grid-area:extra}
.fnl-wrap{display:grid;grid-template-columns:1.05fr .95fr;grid-template-areas:"head card" "extra card";grid-template-rows:min-content 1fr;gap:0 clamp(24px,4vw,64px);align-items:start;padding:clamp(12px,4vh,48px) clamp(16px,4vw,48px) 56px;max-width:1240px;margin:0 auto}
@media(max-width:900px){.fnl-wrap{grid-template-columns:minmax(0,1fr);grid-template-areas:"head" "card" "extra";row-gap:24px;padding-top:8px}.fnl-badges .fnl-badge{display:none}.fnl-sub{margin-bottom:0}}
.fnl-badges{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:16px}
.fnl-badge,.fnl-eyebrow{display:inline-flex;align-items:center;padding:7px 14px;border-radius:999px;font-size:12px;font-weight:700;letter-spacing:.12em;text-transform:uppercase}
.fnl-badge{background:rgba(10,18,32,.55);-webkit-backdrop-filter:blur(10px);backdrop-filter:blur(10px);border:1px solid var(--line)}
.fnl-eyebrow{background:var(--gold);color:var(--ink)}
.fnl-h1{font-size:clamp(28px,4.2vw,52px);line-height:1.06;font-weight:800;letter-spacing:-.03em;margin:0 0 16px;text-shadow:0 4px 30px rgba(0,0,0,.35)}
.fnl-h1-accent{background:linear-gradient(90deg,var(--gold),var(--orange),var(--gold));background-size:200% auto;-webkit-background-clip:text;background-clip:text;color:transparent;animation:fnl-sheen 5s linear infinite;filter:drop-shadow(0 2px 10px rgba(0,0,0,.4))}
@keyframes fnl-sheen{to{background-position:200% center}}
.fnl-sub{font-size:clamp(16px,1.5vw,18px);line-height:1.55;color:var(--muted);max-width:560px;margin:0 0 20px}
.fnl-bullets{list-style:none;padding:0;margin:0 0 20px;display:grid;gap:10px;max-width:560px}
.fnl-bullets li{display:flex;gap:12px;align-items:flex-start;font-size:15px;line-height:1.5;padding:12px 14px;border-radius:14px;background:rgba(255,255,255,.06);border:1px solid var(--line)}
.fnl-check-circle{flex:0 0 24px;width:24px;height:24px;border-radius:50%;background:var(--green);color:#fff;display:grid;place-items:center}
.fnl-zillow-pro{display:inline-flex;align-items:center;gap:12px;padding:12px 16px;border-radius:14px;background:rgba(10,18,32,.6);border:1px solid rgba(253,191,0,.45)}
.fnl-zillow-icon{color:var(--gold);display:inline-flex}
.fnl-zillow-title{font-size:15px;font-weight:700}
.fnl-zillow-sub{font-size:12px;font-weight:600;color:#9fc3ef;margin-top:2px}

/* FORM CARD */
.fnl-card{position:relative;background:var(--glass);-webkit-backdrop-filter:blur(22px) saturate(140%);backdrop-filter:blur(22px) saturate(140%);border-radius:24px;padding:26px;box-shadow:0 30px 80px rgba(0,0,0,.45);scroll-margin-top:84px}
.fnl-card::before{content:"";position:absolute;inset:0;border-radius:24px;padding:1px;background:linear-gradient(135deg,rgba(26,130,255,.9),rgba(255,255,255,.12) 40%,rgba(253,191,0,.9));-webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);-webkit-mask-composite:xor;mask-composite:exclude;pointer-events:none}
@media(min-width:901px){.fnl-card{position:sticky;top:96px}}
.fnl-steplbl{display:flex;justify-content:space-between;font-size:12px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);margin-bottom:8px}
.fnl-steplbl span:last-child{color:var(--gold)}
.fnl-bar{height:5px;border-radius:5px;background:rgba(255,255,255,.12);overflow:hidden;margin-bottom:18px}
.fnl-bar i{display:block;height:100%;border-radius:5px;background:linear-gradient(90deg,var(--blue),var(--gold));transition:width .5s cubic-bezier(.2,.8,.2,1)}
.fnl-q{margin-bottom:16px}
.fnl-q-label{font-size:15px;font-weight:800;margin-bottom:9px}
.fnl-q-options{display:grid;grid-template-columns:1fr 1fr;gap:8px}
.fnl-q-options button{display:flex;align-items:center;justify-content:space-between;gap:8px;text-align:left;padding:12px;border-radius:12px;border:1px solid var(--line);background:rgba(255,255,255,.06);font-size:14px;font-weight:700;cursor:pointer;transition:.15s}
.fnl-q-options button:hover{border-color:rgba(255,255,255,.35)}
.fnl-dot{width:18px;height:18px;border-radius:50%;border:1.5px solid rgba(255,255,255,.35);flex:none;display:grid;place-items:center}
.fnl-q-options button.sel{border-color:var(--blue);background:rgba(26,130,255,.2);box-shadow:0 0 0 1px var(--blue)}
.fnl-q-options button.sel .fnl-dot{background:var(--gold);border-color:var(--gold)}
.fnl-q-options button.sel .fnl-dot::after{content:"";width:6px;height:6px;border-radius:50%;background:var(--ink)}
.fnl-step2-msg{display:flex;gap:10px;align-items:flex-start;font-size:14px;font-weight:600;line-height:1.45;padding:12px 14px;border-radius:12px;background:rgba(4,120,87,.18);border:1px solid rgba(16,185,129,.5);color:#fff}
.fnl-step2-msg svg{flex:0 0 18px;color:#34d399;margin-top:1px}
.fnl-row{display:grid;grid-template-columns:1fr 1fr;gap:10px}
@media(max-width:480px){.fnl-row{grid-template-columns:1fr}}
.fnl-field{position:relative;margin-top:10px}
.fnl-field input{width:100%;height:54px;padding:22px 14px 8px;border-radius:14px;border:1px solid var(--line);background:rgba(255,255,255,.07);outline:none;font-size:16px;transition:.2s}
.fnl-field input:focus{border-color:var(--blue);background:rgba(26,130,255,.1);box-shadow:0 0 0 4px rgba(26,130,255,.18)}
.fnl-field label{position:absolute;left:14px;top:17px;font-size:15px;color:var(--muted);pointer-events:none;transition:.18s}
.fnl-field input:focus+label,.fnl-field input:not(:placeholder-shown)+label{top:7px;font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--gold)}
.fnl-field.invalid input{border-color:#ff5a5a;background:rgba(255,90,90,.08)}
.fnl-cta{position:relative;overflow:hidden;width:100%;margin-top:16px;border:0;border-radius:14px;padding:16px;font-size:17px;font-weight:800;color:#fff;background:linear-gradient(90deg,var(--blue),#3d8bff);cursor:pointer;box-shadow:0 12px 30px rgba(26,130,255,.45);transition:transform .1s}
.fnl-cta:active:not(:disabled){transform:translateY(1px)}
.fnl-cta:disabled{opacity:.7;cursor:wait}
.fnl-cta::after{content:"";position:absolute;top:0;left:-60%;width:40%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,.45),transparent);transform:skewX(-20deg);animation:fnl-shine 3.2s ease-in-out infinite}
@keyframes fnl-shine{0%,60%{left:-60%}100%{left:130%}}
.fnl-nudge,.fnl-err{color:#ff8a8a;font-size:13px;font-weight:600;text-align:center;margin:10px 0 0}
.fnl-back{background:none;border:0;color:var(--muted);font-size:13px;font-weight:600;cursor:pointer;margin:12px auto 0;display:block}
.fnl-micro{font-size:11px;line-height:1.5;color:rgba(255,255,255,.55);text-align:center;margin:12px 0 0}
.fnl-trust{display:flex;justify-content:space-around;gap:8px;flex-wrap:wrap;margin-top:16px;padding-top:14px;border-top:1px solid var(--line)}
.fnl-trust span{display:inline-flex;align-items:center;gap:6px;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--muted)}
.fnl-trust svg{color:var(--gold)}

/* THANK-YOU */
.fnl-thanks{text-align:center;padding:12px 4px 4px}
.fnl-ring svg{width:84px;height:84px}
.fnl-ring circle{stroke-dasharray:240;stroke-dashoffset:240;animation:fnl-draw .8s ease forwards}
.fnl-ring path{stroke-dasharray:60;stroke-dashoffset:60;animation:fnl-draw .5s .6s ease forwards}
@keyframes fnl-draw{to{stroke-dashoffset:0}}
.fnl-thanks h2{font-size:30px;font-weight:800;letter-spacing:-.02em;margin:14px 0 8px}
.fnl-thanks p{color:var(--muted);line-height:1.55;margin:0}
.fnl-zillow-cta{display:inline-block;margin-top:18px;padding:13px 22px;border-radius:12px;background:var(--gold);color:var(--ink);font-weight:800;text-decoration:none}
.fnl-fade{animation:fnl-fade .45s cubic-bezier(.2,.8,.2,1)}
@keyframes fnl-fade{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}

/* LISTINGS — IdxListings keeps its own light styling inside a rounded white band */
.fnl-listings-wrap{max-width:1240px;margin:0 auto;padding:0 clamp(16px,4vw,48px) 48px}
.fnl-listings-band{background:#ffffff;color:#1d2e42;border-radius:24px;padding:32px 24px 36px}
.fnl-listings-inner{max-width:100%;margin:0 auto}
@media(max-width:560px){.fnl-listings-wrap{padding:0 0 40px}.fnl-listings-band{border-radius:20px;padding:28px 0 32px}.fnl-listings-inner{padding:0 16px}}

/* FOOTER */
.fnl-footer{max-width:1240px;margin:0 auto;padding:22px clamp(16px,4vw,48px) 48px;border-top:1px solid var(--line);display:flex;gap:16px;justify-content:space-between;align-items:center;flex-wrap:wrap}
.fnl-logo-foot{height:26px;filter:none}
.fnl-brand-foot{font-size:16px}
.fnl-footer-agent{display:flex;gap:10px;align-items:center;flex-wrap:wrap;font-size:14px;color:var(--muted)}
.fnl-footer-name{color:#fff;font-weight:700}
.fnl-footer-sep{opacity:.35}
.fnl-footer a{color:var(--gold);text-decoration:none}
.fnl-footer a:hover{text-decoration:underline}

/* LOADING / NOT FOUND */
.fnl-loading{min-height:60vh;display:flex;align-items:center;justify-content:center;color:var(--muted);font-size:14px}
.fnl-notfound{max-width:540px;margin:80px auto 0;padding:50px 24px;text-align:center;border-radius:20px;background:rgba(255,255,255,.06);border:1px solid var(--line)}
.fnl-notfound h1{font-size:28px;margin:0 0 8px}
.fnl-notfound p{color:var(--muted);margin:0}

@media(prefers-reduced-motion:reduce){.fnl-bg,.fnl-h1-accent,.fnl-cta::after{animation:none}}
`
